import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { API } from '../App';

const CheckoutSuccess = () => {
  const location = useLocation();
  const [order, setOrder] = useState(location.state?.order || null);
  const [paymentQR, setPaymentQR] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchPaymentQR = async (orderId) => {
    try {
      const response = await fetch(`${API}/orders/${orderId}/payment-qr`);
      
      if (response.ok) {
        const data = await response.json();
        setPaymentQR(data);
      } else {
        console.error('Failed to fetch QR codes:', response.status);
      }
    } catch (error) {
      console.error('Error fetching QR codes:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // If order is already set (from navigation state), fetch QR codes
    if (order?.id) {
      fetchPaymentQR(order.id);
      return;
    }

    // Fallback to persisted order details so the page still works on refresh/direct visit
    const storedOrder = localStorage.getItem('lastOrder');
    if (storedOrder) {
      try {
        const parsedOrder = JSON.parse(storedOrder);
        setOrder(parsedOrder);
        // Fetch QR codes for the restored order
        if (parsedOrder?.id) {
          fetchPaymentQR(parsedOrder.id);
        }
      } catch (error) {
        console.error('Error parsing stored order:', error);
        setLoading(false);
      }
      return;
    }

    // No order found
    setLoading(false);
  }, []);

  if (!order) {
    return (
      <div>
        <Navbar />
        <div className="success-container">
          <div className="no-order">
            <p>No order information found</p>
            <Link to="/">
              <Button>Return to Home</Button>
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div data-testid="checkout-success-page">
      <Navbar />
      
      <div className="success-container">
        <div className="success-card">
          <div className="success-icon">
            <CheckCircle size={80} />
          </div>
          
          <h1 className="success-title">Order Confirmed!</h1>
          <p className="success-subtitle">Thank you for your purchase</p>
          
          <div className="order-number">
            <span className="order-label">Order Number:</span>
            <span className="order-value" data-testid="order-number">{order.orderNumber}</span>
          </div>

          <div className="order-details">
            <h2>Order Summary</h2>
            
            <div className="detail-section">
              <h3>Customer Information</h3>
              <p>{order.customerInfo.firstName} {order.customerInfo.lastName}</p>
              <p>{order.customerInfo.email}</p>
              <p>{order.customerInfo.phone}</p>
            </div>

            <div className="detail-section">
              <h3>Shipping Address</h3>
              <p>{order.shippingAddress.address}</p>
              <p>{order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.zipCode}</p>
              <p>{order.shippingAddress.country}</p>
            </div>

            <div className="detail-section">
              <h3>Items Ordered</h3>
              {order.items.map((item, idx) => (
                <div key={idx} className="order-item">
                  <span>{item.productName} × {item.quantity}</span>
                  <span>${(item.price * item.quantity).toFixed(2)}</span>
                </div>
              ))}
            </div>

            <div className="order-total-section">
              <div className="total-row">
                <span>Subtotal:</span>
                <span>${order.subtotal.toFixed(2)}</span>
              </div>
              <div className="total-row">
                <span>Shipping:</span>
                <span>${order.shipping.toFixed(2)}</span>
              </div>
              <div className="total-row grand-total">
                <span>Total:</span>
                <span>${order.total.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* QR Code Payment Section */}
          {paymentQR && (
            <div className="payment-qr-section">
              <h2 className="qr-title">Complete Your Payment</h2>
              <p className="qr-instructions">{paymentQR.instructions}</p>
              
              <div className="qr-container">
                {/* Cash App QR */}
                <div className="qr-card">
                  <div className="qr-header">
                    <h3>Pay with Cash App</h3>
                    <p className="qr-username">{paymentQR.cashApp.username}</p>
                  </div>
                  <div className="qr-image-wrapper">
                    <img 
                      src={paymentQR.cashApp.qrCode} 
                      alt="Cash App QR Code" 
                      className="qr-image"
                    />
                  </div>
                  <div className="qr-amount">
                    Amount: ${paymentQR.amount.toFixed(2)}
                  </div>
                  <p className="qr-note">Scan with your phone camera or Cash App</p>
                </div>

                {/* Venmo QR */}
                <div className="qr-card">
                  <div className="qr-header">
                    <h3>Pay with Venmo</h3>
                    <p className="qr-username">{paymentQR.venmo.username}</p>
                  </div>
                  <div className="qr-image-wrapper">
                    <img 
                      src={paymentQR.venmo.qrCode} 
                      alt="Venmo QR Code" 
                      className="qr-image"
                    />
                  </div>
                  <div className="qr-amount">
                    Amount: ${paymentQR.amount.toFixed(2)}
                  </div>
                  <p className="qr-note">Scan with your phone camera or Venmo app</p>
                </div>

                {/* Zelle QR */}
                {paymentQR.zelle && (
                  <div className="qr-card">
                    <div className="qr-header">
                      <h3>Pay with Zelle</h3>
                      <p className="qr-username">{paymentQR.zelle.displayInfo}</p>
                    </div>
                    <div className="qr-image-wrapper">
                      <img 
                        src={paymentQR.zelle.qrCode} 
                        alt="Zelle QR Code" 
                        className="qr-image"
                      />
                    </div>
                    <div className="qr-amount">
                      Amount: ${paymentQR.amount.toFixed(2)}
                    </div>
                    {paymentQR.zelle.email && (
                      <p className="zelle-info">Email: {paymentQR.zelle.email}</p>
                    )}
                    {paymentQR.zelle.phone && (
                      <p className="zelle-info">Phone: {paymentQR.zelle.phone}</p>
                    )}
                    <p className="qr-note">Scan QR code and manually enter the amount</p>
                  </div>
                )}
              </div>

              <div className="payment-reminder">
                <strong>Important:</strong> Please include Order #{order.orderNumber} in your payment note.
                We'll confirm your order once payment is received.
              </div>
            </div>
          )}

          <div className="success-notice">
            <p>You can track your order status by contacting our support team with your order number.</p>
          </div>

          <div className="success-actions">
            <Link to="/">
              <Button data-testid="continue-shopping">Continue Shopping</Button>
            </Link>
          </div>
        </div>
      </div>

      <Footer />

      <style jsx>{`
        .success-container {
          margin-top: 80px;
          padding: 3rem 2rem;
          max-width: 900px;
          margin-left: auto;
          margin-right: auto;
          min-height: calc(100vh - 200px);
        }

        .success-card {
          background: white;
          padding: 3rem;
          border-radius: 20px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
          text-align: center;
        }

        .success-icon {
          color: #38A169;
          margin-bottom: 2rem;
          display: flex;
          justify-content: center;
        }

        .success-title {
          font-size: 2.5rem;
          font-weight: 700;
          color: #2D3748;
          margin-bottom: 0.5rem;
          font-family: 'Playfair Display', serif;
        }

        .success-subtitle {
          font-size: 1.2rem;
          color: #718096;
          margin-bottom: 2rem;
        }

        .order-number {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 1rem;
          padding: 1.5rem;
          background: linear-gradient(135deg, rgba(255, 155, 155, 0.1) 0%, rgba(180, 167, 255, 0.1) 100%);
          border-radius: 15px;
          margin-bottom: 2rem;
        }

        .order-label {
          font-weight: 600;
          color: #4A5568;
        }

        .order-value {
          font-size: 1.3rem;
          font-weight: 700;
          color: #FF9B9B;
        }

        .order-details {
          text-align: left;
          margin-bottom: 2rem;
        }

        .order-details h2 {
          font-size: 1.5rem;
          font-weight: 700;
          color: #2D3748;
          margin-bottom: 1.5rem;
          font-family: 'Playfair Display', serif;
          text-align: center;
        }

        .detail-section {
          padding: 1.5rem;
          background: #F7FAFC;
          border-radius: 10px;
          margin-bottom: 1rem;
        }

        .detail-section h3 {
          font-size: 1.1rem;
          font-weight: 600;
          color: #2D3748;
          margin-bottom: 0.75rem;
        }

        .detail-section p {
          color: #4A5568;
          line-height: 1.6;
          margin-bottom: 0.25rem;
        }

        .order-item {
          display: flex;
          justify-content: space-between;
          padding: 0.75rem 0;
          border-bottom: 1px solid #E2E8F0;
        }

        .order-item:last-child {
          border-bottom: none;
        }

        .order-total-section {
          padding: 1.5rem;
          background: #F7FAFC;
          border-radius: 10px;
          margin-bottom: 2rem;
        }

        .total-row {
          display: flex;
          justify-content: space-between;
          padding: 0.5rem 0;
          color: #4A5568;
        }

        .grand-total {
          font-size: 1.3rem;
          font-weight: 700;
          color: #2D3748;
          padding-top: 1rem;
          margin-top: 1rem;
          border-top: 2px solid #E2E8F0;
        }

        .success-notice {
          padding: 1.5rem;
          background: #E6FFFA;
          border-radius: 10px;
          margin-bottom: 2rem;
        }

        .success-notice p {
          color: #2C7A7B;
          line-height: 1.6;
          margin-bottom: 0.5rem;
        }

        .success-notice p:last-child {
          margin-bottom: 0;
        }

        .success-actions {
          display: flex;
          justify-content: center;
          gap: 1rem;
        }

        .no-order {
          text-align: center;
          padding: 4rem 2rem;
        }

        .no-order p {
          font-size: 1.2rem;
          color: #718096;
          margin-bottom: 2rem;
        }

        /* QR Code Payment Styles */
        .payment-qr-section {
          margin: 3rem 0;
          padding: 2rem;
          background: linear-gradient(135deg, rgba(255, 155, 155, 0.05) 0%, rgba(180, 167, 255, 0.05) 100%);
          border-radius: 15px;
          border: 2px solid #E2E8F0;
        }

        .qr-title {
          font-size: 1.8rem;
          font-weight: 700;
          color: #2D3748;
          text-align: center;
          margin-bottom: 1rem;
          font-family: 'Playfair Display', serif;
        }

        .qr-instructions {
          text-align: center;
          color: #4A5568;
          font-size: 1.1rem;
          margin-bottom: 2rem;
          line-height: 1.6;
        }

        .qr-container {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 2rem;
          margin-bottom: 2rem;
        }

        .qr-card {
          background: white;
          padding: 2rem;
          border-radius: 15px;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
          text-align: center;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }

        .qr-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
        }

        .qr-header h3 {
          font-size: 1.5rem;
          font-weight: 700;
          color: #2D3748;
          margin-bottom: 0.5rem;
          font-family: 'Playfair Display', serif;
        }

        .qr-username {
          font-size: 1.2rem;
          font-weight: 600;
          color: #FF9B9B;
          margin-bottom: 1.5rem;
        }

        .qr-image-wrapper {
          display: flex;
          justify-content: center;
          align-items: center;
          margin: 1.5rem 0;
          padding: 1rem;
          background: white;
          border-radius: 10px;
        }

        .qr-image {
          max-width: 250px;
          width: 100%;
          height: auto;
          border-radius: 8px;
        }

        .qr-amount {
          font-size: 1.5rem;
          font-weight: 700;
          color: #2D3748;
          margin: 1rem 0;
        }

        .qr-note {
          font-size: 0.9rem;
          color: #718096;
          margin-top: 1rem;
        }

        .zelle-info {
          font-size: 0.95rem;
          color: #4A5568;
          font-weight: 500;
          margin: 0.5rem 0;
        }

        .payment-reminder {
          background: #FFF5E6;
          border-left: 4px solid #FF9B9B;
          padding: 1.5rem;
          border-radius: 8px;
          color: #744210;
          text-align: center;
        }

        .payment-reminder strong {
          color: #FF9B9B;
        }

        @media (max-width: 768px) {
          .success-container {
            padding: 2rem 1rem;
            margin-top: 60px;
          }

          .success-card {
            padding: 2rem 1rem;
          }

          .success-title {
            font-size: 2rem;
          }

          .qr-container {
            grid-template-columns: 1fr;
            gap: 1.5rem;
          }

          .payment-qr-section {
            padding: 1.5rem;
          }

          .qr-title {
            font-size: 1.5rem;
          }

          .qr-image {
            max-width: 200px;
          }
        }
      `}</style>
    </div>
  );
};

export default CheckoutSuccess;
